import React, { useEffect, useState } from 'react';
import { Button, Col, Modal, ModalBody, ModalFooter, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { endOfDay, startOfDay } from 'date-fns';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { recentCallGet } from '../../../store/slices/tenantSlices/realtimeReportSlice';
import FormSelect from '../../../components/form/FormSelect';
import CallexLoader from '../../../components/CallexLoader';
import { useTimezone } from '../../../store/slices/commonSlice';
import { toZonedTime } from 'date-fns-tz';
import { formatPayloadDate } from '../../../utils/common';

const RecentCallModal = ({ isOpen, onCloseClick, processOptions = [] }) => {
  const dispatch = useDispatch();
  const timezone = useTimezone();
  const today = toZonedTime(new Date(), timezone);
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState([]);

  const initialValues = {
    start_date: startOfDay(today),
    end_date: endOfDay(today),
    process_id: '',
    limit: 100
  };

  const validationSchema = Yup.object({
    start_date: Yup.date().required('Start date is required'),
    end_date: Yup.date().required('End date is required')
  });

  const onSubmit = async () => {
    setIsLoading(true);
    try {
      const formattedStartDate = formatPayloadDate(values.start_date);
      const formattedEndDate = formatPayloadDate(values.end_date);
      const payload = {
        ...values
      };
      payload['start_date'] = formattedStartDate;
      payload['process_id'] = values?.process_id || undefined;
      payload['end_date'] = formattedEndDate;

      const response = await dispatch(recentCallGet(payload)).unwrap();
      if (response) {
        setReportData(response || { data: [] });
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const { values, handleChange, handleBlur, handleSubmit, touched, errors } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  useEffect(() => {
    if (isOpen) {
      onSubmit();
    }
  }, [isOpen, values]);

  return (
    <Modal isOpen={isOpen} size="xl" centered backdrop>
      <ModalBody>
        <div className="text-center fw-bolder">Recent Call Details</div>
        <div className="py-2">
          <div className="mb-3 p-3">
            <form onSubmit={handleSubmit}>
              <Row>
                <Col lg="3" md="3" className="flex-row">
                  <FormSelect
                    label="Limit"
                    placeholder="Select Limit"
                    value={values.limit}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    name="limit"
                    options={[
                      { label: '10', value: 10 },
                      { label: '20', value: 20 },
                      { label: '50', value: 50 },
                      { label: '100', value: 100 }
                    ]}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="3" md="3" className="flex-row">
                  <FormSelect
                    label="Process"
                    placeholder="Select Process"
                    value={values.process_id}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    name="process_id"
                    options={[{ label: 'All', value: '' }, ...(processOptions || [])]}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="3" md="3" className="flex-row d-flex">
                  <div className="d-flex justify-content-start align-items-center gap-3">
                    <span>Connected Count :</span>
                    <span className="text-success fw-bold">{reportData?.connected_count || 0}</span>
                  </div>
                </Col>
                <Col lg="3" md="3" className="flex-row d-flex">
                  <div className="d-flex justify-content-start align-items-center gap-3">
                    <span>Not Connected Count :</span>
                    <span className="text-danger fw-bold">
                      {reportData?.not_connected_count || 0}
                    </span>
                  </div>
                </Col>
              </Row>
            </form>
          </div>
          {isLoading ? (
            <div
              className="p-0 d-flex justify-content-center align-items-center m-0"
              style={{ height: 'calc(100vh - 360px)' }}>
              <CallexLoader />
            </div>
          ) : reportData?.data?.length > 0 ? (
            <div className="p-0 position-relative">
              <div className="daily-report-table" style={{ height: 'calc(100vh - 360px)' }}>
                <table className="report-list-table">
                  <thead>
                    <tr>
                      {reportData?.header?.map((val, index) => {
                        return <th key={index}>{val.label}</th>;
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {reportData?.data?.map((val, index) => {
                      return (
                        <tr key={index}>
                          {reportData?.header?.map((value, idx) => {
                            return <td key={idx}>{val[value?.name] || '-'}</td>;
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div
              className="p-0 d-flex justify-content-center align-items-center no-data m-0"
              style={{ height: 'calc(100vh - 360px)' }}>
              No data available
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter className="justify-content-center">
        <Button className="px-5 bg-danger border-danger" onClick={() => onCloseClick()}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

RecentCallModal.propTypes = {
  isOpen: PropTypes.bool,
  processOptions: PropTypes.array,
  onCloseClick: PropTypes.func
};

export default RecentCallModal;
